/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { jsx } from '@amazon/vinyl-tsx'

const DEFAULT_TILE_COUNT = 10

export interface ShovelerSkeletonProps {
    /**
     * Number of placeholder items to render. Defaults to 10, GH's
     * `RecommendationWidgetSkeleton` count; the row clips whatever does not fit.
     */
    readonly count?: number
    /**
     * `tiles` (default): square artwork over one text line, like a visual shoveler.
     * `rows`: a thumbnail beside text lines, like a row of horizontal list items (GH's
     * `RecommendationWidgetSkeleton` `DESCRIPTIVE` widget).
     */
    readonly variant?: 'tiles' | 'rows'
}

/**
 * Shoveler-shaped placeholder for horizontal carousels, GH's web
 * `RecommendationWidgetSkeleton`: a title bar and a "see more" pill over a
 * row of placeholder items, painted as a flat fill (`.shovelerSkeleton .shimmer`).
 *
 * Usage:
 * ```tsx
 * <ShovelerSkeleton />                       // 10 tiles (default)
 * <ShovelerSkeleton count={6} />             // 6 tiles
 * <ShovelerSkeleton count={4} variant="rows" /> // 4 list rows
 * ```
 */
export function ShovelerSkeleton(props?: ShovelerSkeletonProps | null) {
    const count = props?.count ?? DEFAULT_TILE_COUNT
    const rows = props?.variant === 'rows'
    return (
        <div
            className={rows ? 'shovelerSkeleton shovelerSkeleton--rows' : 'shovelerSkeleton'}
            aria-hidden="true"
        >
            <div className="titleRow">
                <div className="title shimmer" />
                <div className="seeMore shimmer" />
            </div>
            <div className="row">
                {...Array.from({ length: count }, () =>
                    rows ? <ShovelerSkeletonListRow /> : <ShovelerSkeletonTile />
                )}
            </div>
        </div>
    )
}

export function ShovelerSkeletonTile() {
    return (
        <div className="tile">
            <div className="image shimmer" />
            <div className="text shimmer" />
        </div>
    )
}

export function ShovelerSkeletonListRow() {
    return (
        <div className="listRow">
            <div className="image shimmer" />
            <div className="details">
                <div className="text shimmer" />
                <div className="subtext shimmer" />
                <div className="meta">
                    <div className="metaItem shimmer" />
                    <div className="metaItem shimmer" />
                </div>
            </div>
        </div>
    )
}
